import { Dispatch, FC, SetStateAction } from 'react'
import { Label } from '@progress/kendo-react-labels'
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs'
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import { StackLayout } from '@progress/kendo-react-layout'
import { produce } from 'immer'

import {
  FiscalYearDropDownList,
  BuildingUsecaseDropDownList,
  BranchSelect,
  ProcurementStatusSelect,
  ObtainHistorySelect,
  ConstrFormSelect,
  EvaluationSelect,
  StatusSelect,
  NormalStatusSelect,
  StructureTypeSelect,
  ConstructionPeriodYearsDropDownList,
  FromToNumberInput,
  ConstrTypeSelect,
  AvailabilityStatusSelect,
  EffortLevelSelect,
  FromToInput
  // ExpenseSelect
} from '../../../components/parts'
import { UpdaterClassificationSelect } from '../../../components/parts/UpdaterClassificationSelect'
import { GetBuildingSearchReqObj } from '@gateway.Building'
import { Authority } from '../../../gateway/system/Authority'
import { UserProperty } from '../../../gateway/system/UserProperty'
import { useAppSelector } from '../../../stores'

interface BuildingSearchConditionProps {
  searchConditionState: GetBuildingSearchReqObj
  setSearchConditionState: Dispatch<SetStateAction<GetBuildingSearchReqObj>>
  userProp?: UserProperty
  disabledConditionYamadumiFlg: boolean
  disabledConditionKenzaiDiscloseFlg: boolean
}

export const BuildingSearchCondition: FC<BuildingSearchConditionProps> = ({
  searchConditionState,
  setSearchConditionState,
  userProp,
  disabledConditionYamadumiFlg,
  disabledConditionKenzaiDiscloseFlg
}) => {
  /**
   * 調達番号
   * @param event
   */
  const handleProcurementNo = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.procurementNo = event.value
      })
    )
  }

  /**
   * 物件番号
   * @param event
   */
  const handleBuildingNo = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.buildingNo = event.value
      })
    )
  }

  /**
   * 物件番号有無
   * @param event
   */
  const handleBuildingNoExist = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.buildingNoExist = valNumArray
      })
    )
  }

  /**
   * 工事科目コード有無
   * @param event
   */
  const handleConstructionSubjectCdExist = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.constructionSubjectCdExist = valNumArray
      })
    )
  }

  /**
   * 作業所コード有無
   * @param event
   */
  const handleSagyosyoCdExist = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.sagyosyoCdExist = valNumArray
      })
    )
  }

  /**
   * 選択と集中対象
   * @param event
   */
  const handleChoiceAndConcentrationTarget = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.choiceAndConcentrationTarget = valNumArray
      })
    )
  }

  /**
   * 入手年度
   * @param event
   */
  const handleObtainFy = (year: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.obtainFyList = year
      })
    )
  }

  /**
   * 入手経緯
   * @param event
   */
  const handleObtainHistoryCdList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.obtainHistoryCdList = value
      })
    )
  }

  /**
   * 施工形式
   * @param event
   */
  const handleConstructionFormCdList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.constructionFormCdList = value
      })
    )
  }

  /**
   * 評価
   * @param event
   */
  const handleEvaluationValueCdList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.evaluationValueCdList = value
      })
    )
  }

  /**
   * 取組レベル
   * @param event
   */
  const handleEffortLevelCdList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.effortLevelCdList = value
      })
    )
  }

  /**
   * 調達状況
   * @param event
   */
  const handleProcurementStsList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.procurementStsList = value
      })
    )
  }

  /**
   * 更新ステータス
   * @param event
   */
  const handleUpdStsList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.updStsList = value
      })
    )
  }

  /**
   * 山積対象フラグ
   * @param event
   */
  const handleYamadumiFlg = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.yamadumiFlg = valNumArray
      })
    )
  }

  /**
   * 建材部公開フラグ
   * @param event
   */
  const handleKenzaiDiscloseFlg = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.kenzaiDiscloseFlg = valNumArray
      })
    )
  }

  /**
   * 業者公開フラグ
   * @param event
   */
  const handleSupplierDiscloseFlg = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.supplierDiscloseFlg = valNumArray
      })
    )
  }

  /**
   * 竣工済フラグ
   * @param event
   */
  const handleCompletedFlg = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.completedFlg = valNumArray
      })
    )
  }

  /**
   * 帳票対象外フラグ
   * @param event
   */
  const handleReportNonTargetFlg = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.reportNonTargetFlg = valNumArray
      })
    )
  }

  /**
   * 更新者区分
   * @param event
   */
  const handleUpdUserKbn = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.updUserKbn = value
      })
    )
  }

  /**
   * 更新者名
   * @param event
   */
  const handleUpdUserNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.updUserStr = event.value
      })
    )
  }

  /**
   * 更新日（開始）
   * @param event
   */
  const handleUpdDateFrom = (value: string) => {
    setSearchConditionState(
      produce((draft) => {
        draft.updDateFrom = value
      })
    )
  }

  /**
   * 更新日（終了）
   * @param event
   */
  const handleUpdDateTo = (value: string) => {
    setSearchConditionState(
      produce((draft) => {
        draft.updDateTo = value
      })
    )
  }

  /**
   * 支店
   * @param event
   */
  const handleBranchCdList = (valueList: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.branchCdList = valueList
      })
    )
  }

  /**
   * 工事名
   * @param event
   */
  const handleConstructionNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.constructionNm = event.value
      })
    )
  }

  /**
   * 工事名重複
   * @param event
   */
  const handleConstructionNmDuplication = (value: string[]) => {
    const valNumArray = value.map((val) => {
      return Number(val)
    })
    setSearchConditionState(
      produce((draft) => {
        draft.constructionNmDuplication = valNumArray
      })
    )
  }

  /**
   * 工事部名
   * @param event
   */
  const handleConstructionSectionNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.constructionSectionNm = event.value
      })
    )
  }

  /**
   * 所長担当者名
   * @param event
   */
  const handleChiefPersonNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.chiefPersonNm = event.value
      })
    )
  }

  /**
   * 本社営業部名
   * @param event
   */
  const handleHqSalesSectionNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.hqSalesSectionNm = event.value
      })
    )
  }

  /**
   * 本社営業担当者名
   * @param event
   */
  const handleHqSalesPersonNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.hqSalesPersonNm = event.value
      })
    )
  }

  /**
   * 支店営業部名
   * @param event
   */
  const handleBranchSalesSectionNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.branchSalesSectionNm = event.value
      })
    )
  }

  /**
   * 支店営業担当者名
   * @param event
   */
  const handleBranchSalesPersonNm = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.branchSalesPersonNm = event.value
      })
    )
  }

  /**
   * 工事種別
   * @param event
   */
  const handleConstructionCdList = (valueList: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.constructionCdList = valueList
      })
    )
  }

  /**
   * 建物用途
   * @param event
   */
  const handleArchitectureUse = (codeValue: string) => {
    setSearchConditionState(
      produce((draft) => {
        draft.architectureUse = codeValue
      })
    )
  }

  /**
   *地下
   * @param event
   */
  const handleStructureUnderGroundList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.structureUndergroundList = value
      })
    )
  }

  /**
   * 地上
   * @param event
   */
  const handleStructureGroundList = (value: string[]) => {
    setSearchConditionState(
      produce((draft) => {
        draft.structureGroundList = value
      })
    )
  }

  /**
   * 備考
   * @param event
   */
  const handleRemark = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.remark = event.value
      })
    )
  }

  /**
   * 総工事価格（下限）
   * @param event
   */
  const handleTotalCostFrom = (value: number | null) => {
    setSearchConditionState(
      produce((draft) => {
        draft.totalCostFrom = value
      })
    )
  }

  /**
   * 総工事価格（上限）
   * @param event
   */
  const handleTotalCostTo = (value: number | null) => {
    setSearchConditionState(
      produce((draft) => {
        draft.totalCostTo = value
      })
    )
  }

  /**
   * 当社概算工事価格（下限）
   * @param event
   */
  const handleOurCompanyTotalCostFrom = (value: number | null) => {
    setSearchConditionState(
      produce((draft) => {
        draft.ourCompanyTotalCostFrom = value
      })
    )
  }

  /**
   * 当社概算工事価格（上限）
   * @param event
   */
  const handleOurCompanyTotalCostTo = (value: number | null) => {
    setSearchConditionState(
      produce((draft) => {
        draft.ourCompanyTotalCostTo = value
      })
    )
  }

  /**
   * 工期（開始）
   * @param event
   */
  const handleScheduleYFrom = (value: number) => {
    setSearchConditionState(
      produce((draft) => {
        draft.scheduleYFrom = String(value)
      })
    )
  }

  /**
   * 工期（終了）
   * @param event
   */
  const handleScheduleYTo = (value: number) => {
    setSearchConditionState(
      produce((draft) => {
        draft.scheduleYTo = String(value)
      })
    )
  }

  /**
   * 特殊工法
   * @param event
   */
  const handleSpecialConstructionMethodStr = (event: InputChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.specialConstructionMethod = event.value
      })
    )
  }

  // /**
  //  * 費目要否ON
  //  */
  // const handleExpenseOnCdList = (valueList: string[]) => {
  //   setSearchConditionState(
  //     produce((draft) => {
  //       draft.expenseOnCdList = valueList
  //     })
  //   )
  // }

  // /**
  //  * 費目要否OFF
  //  */
  // const handleExpenseOffCdList = (valueList: string[]) => {
  //   setSearchConditionState(
  //     produce((draft) => {
  //       draft.expenseOffCdList = valueList
  //     })
  //   )
  // }

  /**
   * ソート順
   * @param event
   */
  const handleSortOrder = (event: DropDownListChangeEvent) => {
    setSearchConditionState(
      produce((draft) => {
        draft.sortOrder = event.value.CODE
      })
    )
  }

  /** ソート順の取得 */
  const createSortOrderValue = (codeValue?: string) => {
    if (codeValue === undefined) {
      return null
    }
    const _value = sortOrder.find((codeName) => codeName.CODE === codeValue)
    return _value === undefined ? null : _value
  }

  const initialData = useAppSelector((x) => x.common.initialData)
  const sortOrder = initialData.codeNames('SO02')

  const isSupplier = userProp?.authority === Authority.Supplier

  return (
    <>
      <h3>検索条件</h3>
      <StackLayout gap={5} orientation="vertical">
        <Label>調達番号</Label>
        <Input
          onChange={handleProcurementNo}
          value={searchConditionState.procurementNo}
        />

        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label>物件番号</Label>
            <Input
              onChange={handleBuildingNo}
              value={searchConditionState.buildingNo}
            />

            <Label style={{ color: '#424242' }}>物件番号有無</Label>
            <AvailabilityStatusSelect
              multiple
              value={searchConditionState.buildingNoExist.map((item) => {
                return String(item)
              })}
              onChange={handleBuildingNoExist}
            />

            <Label>工事科目コード有無</Label>
            <AvailabilityStatusSelect
              multiple
              value={searchConditionState.constructionSubjectCdExist.map((item) => {
                return String(item)
              })}
              onChange={handleConstructionSubjectCdExist}
            />

            <Label>作業所コード有無</Label>
            <AvailabilityStatusSelect
              multiple
              value={searchConditionState.sagyosyoCdExist.map((item) => {
                return String(item)
              })}
              onChange={handleSagyosyoCdExist}
            />

            <Label>選択と集中</Label>
            <AvailabilityStatusSelect
              multiple
              value={searchConditionState.choiceAndConcentrationTarget.map((item) => {
                return String(item)
              })}
              onChange={handleChoiceAndConcentrationTarget}
            />

            <Label>備考</Label>
            <Input
              onChange={handleRemark}
              value={searchConditionState.remark}
            />

            <Label>入手年度</Label>
            <FiscalYearDropDownList
              multiple
              origin={-5}
              range={16}
              fyddlValueList={
                searchConditionState.obtainFyList.length > 0
                  ? searchConditionState.obtainFyList
                  : []
              }
              onYearChangeList={handleObtainFy}
            />

            <Label>入手経緯</Label>
            <ObtainHistorySelect
              multiple
              value={searchConditionState.obtainHistoryCdList}
              onChange={handleObtainHistoryCdList}
            />

            <Label>施工形式</Label>
            <ConstrFormSelect
              multiple
              value={searchConditionState.constructionFormCdList}
              onChange={handleConstructionFormCdList}
            />

            <Label>評価</Label>
            <EvaluationSelect
              multiple
              value={searchConditionState.evaluationValueCdList}
              onChange={handleEvaluationValueCdList}
            />

            <Label>取組レベル</Label>
            <EffortLevelSelect
              multiple
              value={searchConditionState.effortLevelCdList}
              onChange={handleEffortLevelCdList}
            />
          </>
            )}

        <Label>調達状況</Label>
        <ProcurementStatusSelect
          multiple
          value={searchConditionState.procurementStsList}
          onChange={handleProcurementStsList}
        />

        <Label>更新ステータス</Label>
        <StatusSelect
          multiple
          onChange={handleUpdStsList}
          value={searchConditionState.updStsList}
        />

        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label>山積対象選択</Label>
            <NormalStatusSelect
              multiple
              value={searchConditionState.yamadumiFlg.map((item) => {
                return String(item)
              })}
              onChange={handleYamadumiFlg}
              disabled={disabledConditionYamadumiFlg}
            />

            <Label>建材部公開</Label>
            <NormalStatusSelect
              multiple
              value={searchConditionState.kenzaiDiscloseFlg.map((item) => {
                return String(item)
              })}
              onChange={handleKenzaiDiscloseFlg}
              disabled={disabledConditionKenzaiDiscloseFlg}
            />

            <Label>業者公開</Label>
            <NormalStatusSelect
              multiple
              value={searchConditionState.supplierDiscloseFlg.map((item) => {
                return String(item)
              })}
              onChange={handleSupplierDiscloseFlg}
            />

            <Label style={{ color: '#424242' }}>竣工済</Label>
            <NormalStatusSelect
              multiple
              value={searchConditionState.completedFlg.map((item) => {
                return String(item)
              })}
              onChange={handleCompletedFlg}
            />

            <Label style={{ color: '#424242' }}>帳票対象外</Label>
            <NormalStatusSelect
              multiple
              value={searchConditionState.reportNonTargetFlg.map((item) => {
                return String(item)
              })}
              onChange={handleReportNonTargetFlg}
            />

            <Label>更新者区分</Label>
            <UpdaterClassificationSelect
              multiple
              onChange={handleUpdUserKbn}
              value={searchConditionState.updUserKbn}
            />

            <Label>更新者名</Label>
            <Input
              onChange={handleUpdUserNm}
              value={searchConditionState.updUserStr}
            />

            <Label style={{ color: '#424242' }}>更新日（YYYY/MM/DD, YYYYMMDD）</Label>
            <FromToInput
              limitLeftValue={searchConditionState.updDateFrom}
              limitRightValue={searchConditionState.updDateTo}
              onLimitLeftChange={handleUpdDateFrom}
              onLimitRightChange={handleUpdDateTo}
            />
          </>
            )}

        <Label>支店</Label>
        <BranchSelect
          multiple
          onChange={handleBranchCdList}
          value={searchConditionState.branchCdList}
          autoClose={false}
        />

        <Label>工事名</Label>
        <Input
          onChange={handleConstructionNm}
          value={searchConditionState.constructionNm}
        />

        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label style={{ color: '#424242' }}>工事名重複</Label>
            <AvailabilityStatusSelect
              multiple
              value={searchConditionState.constructionNmDuplication.map((item) => {
                return String(item)
              })}
              onChange={handleConstructionNmDuplication}
            />

            <Label>総工事価格（百万）</Label>
            <FromToNumberInput
              onLimitLeftChange={handleTotalCostFrom}
              onLimitRightChange={handleTotalCostTo}
              limitLeftValue={searchConditionState.totalCostFrom ?? undefined}
              limitRightValue={searchConditionState.totalCostTo ?? undefined}
            />

            <Label>当社概算工事価格（百万）</Label>
            <FromToNumberInput
              onLimitLeftChange={handleOurCompanyTotalCostFrom}
              onLimitRightChange={handleOurCompanyTotalCostTo}
              limitLeftValue={
                searchConditionState.ourCompanyTotalCostFrom ?? undefined
              }
              limitRightValue={
                searchConditionState.ourCompanyTotalCostTo ?? undefined
              }
            />
          </>
            )}

        <Label>工期</Label>
        <ConstructionPeriodYearsDropDownList
          startOrigin={-3}
          startRange={14}
          endOrigin={-3}
          endRange={24}
          startYearValue={
            searchConditionState.scheduleYFrom !== undefined &&
            searchConditionState.scheduleYFrom !== ''
              ? Number(searchConditionState.scheduleYFrom)
              : undefined
          }
          endYearValue={
            searchConditionState.scheduleYTo !== undefined &&
            searchConditionState.scheduleYTo !== ''
              ? Number(searchConditionState.scheduleYTo)
              : undefined
          }
          onStartYearChange={handleScheduleYFrom}
          onEndYearChange={handleScheduleYTo}
          clear={true}
        />

        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label>工事種別</Label>
            <ConstrTypeSelect
              multiple
              onChange={handleConstructionCdList}
              value={searchConditionState.constructionCdList}
              clear={true}
            />
          </>
            )}

        <Label>建物用途</Label>
        <BuildingUsecaseDropDownList
          onChange={handleArchitectureUse}
          value={searchConditionState.architectureUse}
          clear={true}
        />

        <Label>構造（地下）</Label>
        <StructureTypeSelect
          multiple
          onChange={handleStructureUnderGroundList}
          value={searchConditionState.structureUndergroundList}
        />

        <Label>構造（地上）</Label>
        <StructureTypeSelect
          multiple
          onChange={handleStructureGroundList}
          value={searchConditionState.structureGroundList}
        />

        <Label>特殊工法</Label>
        <Input
          onChange={handleSpecialConstructionMethodStr}
          value={searchConditionState.specialConstructionMethod}
        />
{/*
        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label>費目要否ON （※ 複数費目の指定はAND検索）</Label>
            <ExpenseSelect
                multiple
                onChange={handleExpenseOnCdList}
                value={searchConditionState.expenseOnCdList}
            />
            <Label>費目要否OFF（※ 複数費目の指定はAND検索）</Label>
            <ExpenseSelect
                multiple
                onChange={handleExpenseOffCdList}
                value={searchConditionState.expenseOffCdList}
            />
          </>
            )}
*/}
        <Label>工事部</Label>
        <Input
          onChange={handleConstructionSectionNm}
          value={searchConditionState.constructionSectionNm}
        />

        <Label>所長</Label>
        <Input
          onChange={handleChiefPersonNm}
          value={searchConditionState.chiefPersonNm}
        />

        <Label>本社営業担当部</Label>
        <Input
          onChange={handleHqSalesSectionNm}
          value={searchConditionState.hqSalesSectionNm}
        />

        <Label>本社営業担当者</Label>
        <Input
          onChange={handleHqSalesPersonNm}
          value={searchConditionState.hqSalesPersonNm}
        />

        <Label>支店営業担当部</Label>
        <Input
          onChange={handleBranchSalesSectionNm}
          value={searchConditionState.branchSalesSectionNm}
        />

        <Label>支店営業担当者</Label>
        <Input
          onChange={handleBranchSalesPersonNm}
          value={searchConditionState.branchSalesPersonNm}
        />

        {isSupplier
          ? (
          <></>
            )
          : (
          <>
            <Label style={{ color: '#424242' }}>ソート順</Label>
            <DropDownList
              data={sortOrder}
              dataItemKey="CODE"
              textField="NAME"
              value={createSortOrderValue(searchConditionState.sortOrder)}
              onChange={handleSortOrder}
            />
          </>
            )}
      </StackLayout>
    </>
  )
}
